import { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import PivotConfigurator from '../components/Mfp/PivotConfigurator/PivotConfigurator';
import { ConfigItem } from '../components/Mfp/PivotConfigurator/PivotConfigurator.types';
import { findIndex, get, isEmpty, isNil, remove } from 'lodash';
import { API_BASE_URL } from 'src/state/ViewConfig/ViewConfig.slice';
import { DimensionItem, PivotOptions } from 'src/components/Mfp/PivotConfigurator/utils';

export interface FavoritesEntry {
  key: string; // identifier
  group: string; // what facet it applies to
  value: PivotOptions; // the favorites
}

export interface FavoritesPayload {
  favorites: FavoritesEntry[];
}

const REST_STORAGE_KEY = 'user-pivot-layout-favorites';
const emptyFavorites = [] as FavoritesEntry[];
const getFavoritesRoute = (siloId: string) => `${API_BASE_URL}/silo/${siloId}/settings` as const;
const getFavoritesUri = (siloId: string) => `${getFavoritesRoute(siloId)}/${REST_STORAGE_KEY}`;

export default class Favorites {
  protected client: AxiosInstance;

  constructor(client: AxiosInstance) {
    this.client = client;
  }

  public handleFavoritesResponse = (response: AxiosResponse<FavoritesPayload>) => {
    if (typeof response.data === 'string' && response.data !== '') {
      response.data = JSON.parse(response.data);
    }
    const favorites = get(response, 'data.favorites');
    if (isNil(favorites) || isEmpty(favorites)) {
      return emptyFavorites;
    }
    return response.data.favorites;
  };
  public handleFavoritesError = (error: AxiosError) => {
    if (error.response && error.response.status === 404) {
      // api returns 404 when key not found, indicating no current favorites
      return emptyFavorites;
    }
    // TODO do better error handling here
    if (error.response) {
      throw new Error(`
      An error occured downloading favorites
      ${error.response.status}
      ${error.response.statusText}
      `);
    }
    throw new Error('An error occured downloading favorites');
  };

  public ajaxLoad(siloId: string): Promise<FavoritesEntry[]> {
    return this.client
      .get<FavoritesPayload>(getFavoritesUri(siloId))
      .then(this.handleFavoritesResponse)
      .catch(this.handleFavoritesError);
  }

  public ajaxUpdate(favorites: FavoritesEntry[], siloId: string) {
    const payload: FavoritesPayload = {
      // tslint:disable-next-line: object-literal-shorthand
      favorites: favorites,
    };
    const stringifiedPayload = JSON.stringify(payload);

    return this.client
      .put(getFavoritesUri(siloId), stringifiedPayload)
      .then(() => {
        return this.ajaxLoad(siloId);
      })
      .catch(() => emptyFavorites);
  }

  public create(favorite: FavoritesEntry, siloId: string) {
    return this.ajaxLoad(siloId).then((values) => {
      const newValues = [...values, favorite];
      return this.ajaxUpdate(newValues, siloId).then((newFavorites) => {
        return newFavorites;
      });
    });
  }

  public updateFavorite(favorite: FavoritesEntry, siloId: string) {
    return this.ajaxLoad(siloId).then((values) => {
      const favIndex = findIndex(values, (fav) => fav.key === favorite.key);
      values[favIndex] = favorite;
      return this.ajaxUpdate(values, siloId).then((newValues) => {
        return newValues;
      });
    });
  }

  public delete(id: string, siloId: string) {
    return this.ajaxLoad(siloId).then((values) => {
      remove(values, (fav) => {
        return fav.key === id;
      });
      return this.ajaxUpdate(values, siloId).then((newValues) => {
        return newValues;
      });
    });
  }
}
export const configItemToSimpleFavorite = (configItem: ConfigItem): DimensionItem => {
  let items;
  if (
    configItem.children &&
    !isEmpty(configItem.children) &&
    (configItem.id === 'revisions' || configItem.id === 'metrics')
  ) {
    const nonDisabledChildren = configItem.children!.filter((cg) => !cg.disabled);
    items = nonDisabledChildren.map((i) => i.id);
  } else {
    items = PivotConfigurator.getVisibleLevels(configItem).map((id) => `level:${id}`);
  }
  return {
    items,
    dimension: configItem.id,
    hierarchy: configItem.hierarchy,
  };
};
